document.documentElement.classList.add('js');

const optionTokens = (value) => {
  return (value || '')
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((token) => token.length > 2);
};

const mediaMatchesVariant = (itemKey, mediaKey, matchMode = 'exact') => {
  if (!itemKey) return true;
  if (!mediaKey) return false;
  if (itemKey === mediaKey) return true;
  if (matchMode !== 'fuzzy') return false;

  const selectedTokens = optionTokens(mediaKey);
  const itemTokens = optionTokens(itemKey);
  return selectedTokens.some((token) => itemTokens.includes(token));
};

const updateCartCount = (count) => {
  const cartButton = document.querySelector('[data-cart-button]');
  const cartCount = document.querySelector('[data-cart-count]');
  if (!cartButton || !cartCount) return;

  const itemCount = Number(count || 0);
  cartCount.textContent = itemCount;
  cartCount.hidden = itemCount === 0;
  cartButton.setAttribute('aria-label', itemCount > 0 ? `Cart, ${itemCount} items` : 'Cart');

  cartButton.classList.remove('is-bouncing');
  void cartButton.offsetWidth;
  cartButton.classList.add('is-bouncing');
};

const refreshCartCount = async () => {
  const root = window.Shopify?.routes?.root || '/';
  const response = await fetch(`${root}cart.js`, {
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) throw new Error('Unable to refresh cart');

  const cart = await response.json();
  updateCartCount(cart.item_count);
};

const updateBackToTop = () => {
  const button = document.querySelector('.back-to-top');
  if (!button) return;

  button.classList.toggle('is-visible', window.scrollY > 420);
};

const updateFoundationCarousel = (carousel, nextIndex = null) => {
  if (!carousel) return;

  const viewport = carousel.closest('.starter-sets__viewport');
  const cards = Array.from(carousel.querySelectorAll('.starter-set-card'));
  if (!viewport || cards.length === 0) return;

  const cardWidth = cards[0].getBoundingClientRect().width || 1;
  const visibleCount = Math.max(1, Math.floor((viewport.clientWidth + 18) / (cardWidth + 18)));
  const maxIndex = Math.max(0, cards.length - visibleCount);
  const currentIndex = Number(carousel.dataset.carouselIndex || 0);
  const index = Math.min(Math.max(nextIndex ?? currentIndex, 0), maxIndex);
  const offset = cards[index]?.offsetLeft || 0;

  carousel.dataset.carouselIndex = String(index);
  carousel.style.transform = `translateX(${-offset}px)`;

  const section = carousel.closest('.starter-sets');
  section?.querySelectorAll('[data-foundation-carousel-button]').forEach((button) => {
    const direction = Number(button.dataset.direction || 0);
    button.disabled = direction < 0 ? index === 0 : index === maxIndex;
  });
};

const updateProductCarousel = (carousel, nextIndex = null) => {
  if (!carousel) return;

  const viewport = carousel.querySelector('.product-carousel__viewport');
  const track = carousel.querySelector('[data-product-carousel-track]');
  const cards = Array.from(track?.querySelectorAll('.product-card') || []);
  if (!viewport || !track || cards.length === 0) return;

  const gap = Number.parseFloat(window.getComputedStyle(track).columnGap || window.getComputedStyle(track).gap || '0') || 0;
  const cardWidth = cards[0].getBoundingClientRect().width || 1;
  const visibleCount = Math.max(1, Math.floor((viewport.clientWidth + gap) / (cardWidth + gap)));
  const maxIndex = Math.max(0, cards.length - visibleCount);
  const currentIndex = Number(track.dataset.carouselIndex || 0);
  const index = Math.min(Math.max(nextIndex ?? currentIndex, 0), maxIndex);
  const offset = cards[index]?.offsetLeft || 0;

  track.dataset.carouselIndex = String(index);
  track.style.transform = `translateX(${-offset}px)`;

  carousel.querySelectorAll('[data-product-carousel-button]').forEach((button) => {
    button.hidden = maxIndex === 0;
    const direction = Number(button.dataset.direction || 0);
    button.disabled = direction < 0 ? index === 0 : index === maxIndex;
  });
};

const getVisibleProductMedia = (gallery) => {
  return Array.from(gallery?.querySelectorAll('[data-product-media]') || []).filter((media) => !media.hidden);
};

const updateProductGallery = (gallery, nextIndex = null, jump = false) => {
  if (!gallery) return;

  const track = gallery.querySelector('[data-product-gallery-track]') || gallery;
  const mediaItems = getVisibleProductMedia(gallery);
  const maxIndex = Math.max(0, mediaItems.length - 1);
  const inferredIndex = track.clientWidth > 0 ? Math.round(track.scrollLeft / track.clientWidth) : 0;
  const currentIndex = Number(gallery.dataset.mobileGalleryIndex || inferredIndex || 0);
  const index = Math.min(Math.max(nextIndex ?? inferredIndex ?? currentIndex, 0), maxIndex);
  const target = mediaItems[index];

  gallery.dataset.mobileGalleryIndex = String(index);

  if (target && nextIndex !== null) {
    const targetLeft = target.getBoundingClientRect().left - track.getBoundingClientRect().left + track.scrollLeft;
    track.scrollTo({
      left: targetLeft,
      behavior: jump ? 'auto' : 'smooth',
    });
  }

  gallery.querySelectorAll('[data-product-gallery-button]').forEach((button) => {
    button.hidden = mediaItems.length <= 1;
    const direction = Number(button.dataset.direction || 0);
    button.disabled = direction < 0 ? index === 0 : index === maxIndex;
  });

  const counter = gallery.querySelector('[data-product-gallery-count]');
  if (counter) {
    counter.hidden = mediaItems.length <= 1;
    counter.textContent = mediaItems.length > 1 ? `${index + 1} / ${mediaItems.length}` : '';
  }
};

const syncMobileProductAtc = (section, variantInput = null) => {
  const stickyForm = section?.querySelector('[data-mobile-product-atc]');
  if (!stickyForm) return;

  const selectedInput = variantInput || section.querySelector('.main-product__variant-option input:checked');
  const variantIdInput = stickyForm.querySelector('[data-mobile-product-atc-id]');
  const variantLabel = stickyForm.querySelector('[data-mobile-product-atc-variant]');
  const price = stickyForm.querySelector('[data-mobile-product-atc-price]');
  const quantity = stickyForm.querySelector('[data-mobile-product-atc-quantity]');
  const mainQuantity = section.querySelector('.main-product__quantity');
  const button = stickyForm.querySelector('button[type="submit"]');

  if (selectedInput && variantIdInput) {
    variantIdInput.value = selectedInput.value;
  }

  if (selectedInput?.dataset.title && variantLabel) {
    variantLabel.textContent = selectedInput.dataset.title;
  }

  if (selectedInput?.dataset.price && price) {
    price.textContent = selectedInput.dataset.price;
  }

  if (quantity && mainQuantity) {
    quantity.value = mainQuantity.value || '1';
  }

  if (button && selectedInput?.dataset.variantAvailable) {
    const isAvailable = selectedInput.dataset.variantAvailable === 'true';
    button.disabled = !isAvailable;
    button.textContent = isAvailable ? 'Add to cart' : 'Sold out';
  }
};

const updateMobileProductAtcVisibility = () => {
  const stickyForm = document.querySelector('[data-mobile-product-atc]');
  const mainButton = document.querySelector('[data-main-product-atc-button]');
  if (!stickyForm || !mainButton) return;

  if (!window.matchMedia('(max-width: 640px)').matches) {
    stickyForm.classList.remove('is-hidden');
    return;
  }

  const stickyHeight = stickyForm.getBoundingClientRect().height || 0;
  const buttonRect = mainButton.getBoundingClientRect();
  const visibleBottom = window.innerHeight - stickyHeight - 12;
  const mainButtonIsVisible = buttonRect.top < visibleBottom && buttonRect.bottom > 0;

  stickyForm.classList.toggle('is-hidden', mainButtonIsVisible);
};

let activeCarouselSwipe = null;
let suppressCarouselClick = false;

const getSwipeCarousel = (target) => {
  if (!target || target.closest('button, input, select, textarea, label')) return null;

  const foundationCarousel = target.closest('[data-foundation-carousel]');
  if (foundationCarousel) {
    return { type: 'foundation', element: foundationCarousel };
  }

  const productCarousel = target.closest('[data-product-carousel]');
  if (productCarousel) {
    return { type: 'product', element: productCarousel };
  }

  return null;
};

const moveSwipeCarousel = (swipeTarget, direction) => {
  if (!swipeTarget) return;

  if (swipeTarget.type === 'foundation') {
    const currentIndex = Number(swipeTarget.element.dataset.carouselIndex || 0);
    updateFoundationCarousel(swipeTarget.element, currentIndex + direction);
    return;
  }

  if (swipeTarget.type === 'product') {
    const track = swipeTarget.element.querySelector('[data-product-carousel-track]');
    const currentIndex = Number(track?.dataset.carouselIndex || 0);
    updateProductCarousel(swipeTarget.element, currentIndex + direction);
  }
};

const finishCarouselSwipe = (clientX, clientY, event) => {
  if (!activeCarouselSwipe) return;

  const deltaX = clientX - activeCarouselSwipe.x;
  const deltaY = clientY - activeCarouselSwipe.y;
  const isHorizontalSwipe = Math.abs(deltaX) > 42 && Math.abs(deltaX) > Math.abs(deltaY) * 1.35;

  if (isHorizontalSwipe) {
    if (event.cancelable) event.preventDefault();
    moveSwipeCarousel(activeCarouselSwipe.swipeTarget, deltaX < 0 ? 1 : -1);
    suppressCarouselClick = true;
    window.setTimeout(() => {
      suppressCarouselClick = false;
    }, 350);
  }

  activeCarouselSwipe = null;
};

const closeMobileMenu = () => {
  const toggle = document.querySelector('[data-mobile-menu-toggle]');
  const menu = document.querySelector('[data-mobile-menu]');
  if (!toggle || !menu) return;

  toggle.setAttribute('aria-expanded', 'false');
  toggle.setAttribute('aria-label', 'Open navigation');
  menu.classList.remove('is-open');
};

document.addEventListener('click', (event) => {
  const menuToggle = event.target.closest('[data-mobile-menu-toggle]');
  if (menuToggle) {
    const menu = document.getElementById(menuToggle.getAttribute('aria-controls'));
    if (!menu) return;

    const isOpen = menuToggle.getAttribute('aria-expanded') === 'true';
    menuToggle.setAttribute('aria-expanded', String(!isOpen));
    menuToggle.setAttribute('aria-label', isOpen ? 'Open navigation' : 'Close navigation');
    menu.classList.toggle('is-open', !isOpen);
    return;
  }

  const mobileMenu = event.target.closest('[data-mobile-menu]');
  if (mobileMenu && event.target.closest('a')) {
    closeMobileMenu();
    return;
  }

  if (!event.target.closest('[data-site-header]')) {
    closeMobileMenu();
  }

  const option = event.target.closest('[data-product-card-option]');
  if (option) {
    const card = option.closest('[data-product-card]');
    if (!card || option.getAttribute('aria-disabled') === 'true') return;

    card.querySelectorAll('[data-product-card-option]').forEach((button) => {
      button.classList.toggle('is-active', button === option);
    });

    const image = card.querySelector('.product-card__image img');
    if (image && option.dataset.variantImage) {
      image.src = option.dataset.variantImage;
      image.removeAttribute('srcset');
    }

    const price = card.querySelector('[data-product-card-price]');
    if (price && option.dataset.variantPrice) {
      price.textContent = option.dataset.variantPrice;
    }

    const compare = card.querySelector('[data-product-card-compare]');
    if (compare) {
      compare.textContent = option.dataset.variantCompare || '';
      compare.hidden = !option.dataset.variantCompare;
    }

    const variantInput = card.querySelector('[data-product-card-variant-input]');
    if (variantInput && option.dataset.variantId) {
      variantInput.value = option.dataset.variantId;
    }

    const quickAdd = card.querySelector('[data-product-card-quick-add]');
    if (quickAdd) {
      const available = option.dataset.variantAvailable === 'true';
      quickAdd.disabled = !available;
      quickAdd.textContent = available ? 'Quick add' : 'Sold out';
    }

    const link = card.querySelector('[data-product-card-link]');
    if (link && option.dataset.variantId) {
      const baseUrl = link.dataset.baseUrl || link.href;
      const separator = baseUrl.includes('?') ? '&' : '?';
      link.href = `${baseUrl}${separator}variant=${option.dataset.variantId}`;
    }

    return;
  }

  const carouselButton = event.target.closest('[data-foundation-carousel-button]');
  if (carouselButton) {
    const carousel = carouselButton.closest('.starter-sets')?.querySelector('[data-foundation-carousel]');
    const currentIndex = Number(carousel?.dataset.carouselIndex || 0);
    const direction = Number(carouselButton.dataset.direction || 0);
    updateFoundationCarousel(carousel, currentIndex + direction);
    return;
  }

  const productGalleryButton = event.target.closest('[data-product-gallery-button]');
  if (productGalleryButton) {
    const gallery = productGalleryButton.closest('[data-product-gallery]');
    const currentIndex = Number(gallery?.dataset.mobileGalleryIndex || 0);
    const direction = Number(productGalleryButton.dataset.direction || 0);
    updateProductGallery(gallery, currentIndex + direction);
    return;
  }

  const productCarouselButton = event.target.closest('[data-product-carousel-button]');
  if (productCarouselButton) {
    const carousel = productCarouselButton.closest('[data-product-carousel]');
    const track = carousel?.querySelector('[data-product-carousel-track]');
    const currentIndex = Number(track?.dataset.carouselIndex || 0);
    const direction = Number(productCarouselButton.dataset.direction || 0);
    updateProductCarousel(carousel, currentIndex + direction);
    return;
  }

  const button = event.target.closest('[data-quantity-change]');
  if (!button) return;

  const stepper = button.closest('[data-quantity-stepper]');
  const input = stepper?.querySelector('input[name="updates[]"]');
  const form = button.closest('form');
  const updateButton = form?.querySelector('[data-cart-update]');
  if (!input || !form || !updateButton) return;

  const change = Number(button.dataset.quantityChange);
  const min = Number(input.getAttribute('min') || 1);
  const current = Number(input.value || min);
  input.value = Math.max(min, current + change);

  window.clearTimeout(form.cartUpdateTimer);
  form.cartUpdateTimer = window.setTimeout(() => {
    form.requestSubmit(updateButton);
  }, 450);
});

document.addEventListener('keydown', (event) => {
  if (event.key === 'Escape') {
    closeMobileMenu();
  }
});

document.addEventListener(
  'click',
  (event) => {
    if (!suppressCarouselClick) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    suppressCarouselClick = false;
  },
  true
);

document.addEventListener(
  'mousedown',
  (event) => {
    if (event.button !== 0 || !window.matchMedia('(max-width: 900px)').matches) return;

    const swipeTarget = getSwipeCarousel(event.target);
    if (!swipeTarget) return;

    activeCarouselSwipe = {
      swipeTarget,
      mouse: true,
      x: event.clientX,
      y: event.clientY,
    };
  },
  { passive: true }
);

document.addEventListener(
  'mouseup',
  (event) => {
    if (!activeCarouselSwipe?.mouse) return;

    finishCarouselSwipe(event.clientX, event.clientY, event);
  },
  { passive: false }
);

document.addEventListener(
  'pointerdown',
  (event) => {
    if (!window.matchMedia('(max-width: 900px)').matches || event.isPrimary === false) return;

    const swipeTarget = getSwipeCarousel(event.target);
    if (!swipeTarget) return;

    activeCarouselSwipe = {
      swipeTarget,
      pointerId: event.pointerId,
      x: event.clientX,
      y: event.clientY,
    };
  },
  { passive: true }
);

document.addEventListener(
  'pointerup',
  (event) => {
    if (!activeCarouselSwipe || activeCarouselSwipe.pointerId !== event.pointerId) return;

    finishCarouselSwipe(event.clientX, event.clientY, event);
  },
  { passive: false }
);

document.addEventListener('pointercancel', () => {
  activeCarouselSwipe = null;
});

document.addEventListener(
  'touchstart',
  (event) => {
    if (window.PointerEvent) return;
    if (!window.matchMedia('(max-width: 900px)').matches || event.touches.length !== 1) return;

    const swipeTarget = getSwipeCarousel(event.target);
    if (!swipeTarget) return;

    activeCarouselSwipe = {
      swipeTarget,
      x: event.touches[0].clientX,
      y: event.touches[0].clientY,
    };
  },
  { passive: true }
);

document.addEventListener(
  'touchend',
  (event) => {
    if (window.PointerEvent) return;
    if (!activeCarouselSwipe || event.changedTouches.length !== 1) {
      activeCarouselSwipe = null;
      return;
    }

    finishCarouselSwipe(event.changedTouches[0].clientX, event.changedTouches[0].clientY, event);
  },
  { passive: false }
);

const refreshPageUi = () => {
  document.querySelectorAll('[data-foundation-carousel]').forEach((carousel) => updateFoundationCarousel(carousel));
  document.querySelectorAll('[data-product-carousel]').forEach((carousel) => updateProductCarousel(carousel));
  document.querySelectorAll('[data-product-gallery]').forEach((gallery) => updateProductGallery(gallery, 0, true));
  document.querySelectorAll('.shopify-section').forEach((section) => syncMobileProductAtc(section));
  updateMobileProductAtcVisibility();
};

window.addEventListener('scroll', updateBackToTop, { passive: true });
window.addEventListener('scroll', updateMobileProductAtcVisibility, { passive: true });
document.addEventListener('DOMContentLoaded', refreshPageUi);
window.addEventListener('load', updateBackToTop);
window.addEventListener('load', refreshPageUi);
window.addEventListener('resize', () => {
  document.querySelectorAll('[data-foundation-carousel]').forEach((carousel) => updateFoundationCarousel(carousel));
  document.querySelectorAll('[data-product-carousel]').forEach((carousel) => updateProductCarousel(carousel));
  document.querySelectorAll('[data-product-gallery]').forEach((gallery) => updateProductGallery(gallery, null, true));
  updateMobileProductAtcVisibility();
});
updateBackToTop();
updateMobileProductAtcVisibility();

document.addEventListener(
  'scroll',
  (event) => {
    const gallery = event.target?.closest?.('[data-product-gallery-track]')?.closest('[data-product-gallery]');
    if (!gallery) return;

    window.requestAnimationFrame(() => updateProductGallery(gallery));
  },
  true
);

document.addEventListener('animationend', (event) => {
  const cartButton = event.target.closest('[data-cart-button]');
  if (!cartButton || event.animationName !== 'cart-added-bounce') return;

  cartButton.classList.remove('is-bouncing');
});

document.addEventListener('submit', async (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement)) return;

  const action = new URL(form.action, window.location.origin);
  if (action.pathname !== '/cart/add') return;

  event.preventDefault();

  const button = form.querySelector('button[type="submit"]');
  const originalText = button?.textContent?.trim();
  if (button) {
    button.disabled = true;
    button.textContent = 'Adding';
  }

  try {
    const response = await fetch(form.action, {
      method: 'POST',
      body: new FormData(form),
      headers: { Accept: 'application/json' },
    });

    if (!response.ok) throw new Error('Unable to add item');

    await refreshCartCount();

    if (button) {
      button.textContent = 'Added';
      window.setTimeout(() => {
        button.disabled = false;
        button.textContent = originalText || 'Add to cart';
      }, 1100);
    }
  } catch (error) {
    if (button) {
      button.disabled = false;
      button.textContent = originalText || 'Add to cart';
    }
    console.error(error);
  }
});

document.addEventListener('change', (event) => {
  const input = event.target.closest('.main-product__variant-option input');
  if (!input) return;

  const section = input.closest('.shopify-section') || document;
  const price = section.querySelector('[data-product-price]');
  const selectedVariantTitle = section.querySelector('[data-selected-variant-title]');

  if (price && input.dataset.price) {
    price.textContent = input.dataset.price;
  }

  const compare = section.querySelector('[data-product-compare]');
  if (compare) {
    compare.textContent = input.dataset.comparePrice || '';
    compare.hidden = !input.dataset.comparePrice;
  }

  if (selectedVariantTitle && input.dataset.title) {
    selectedVariantTitle.textContent = input.dataset.title;
  }

  const submitButton = input.closest('form')?.querySelector('button[type="submit"]');
  if (submitButton && input.dataset.variantAvailable) {
    const isAvailable = input.dataset.variantAvailable === 'true';
    submitButton.disabled = !isAvailable;
    submitButton.textContent = isAvailable ? 'Add to cart' : 'Sold out';
  }

  syncMobileProductAtc(section, input);

  const mediaKey = input.dataset.mediaKey || '';
  const matchMode = input.dataset.mediaMatch || 'exact';
  const mediaItems = Array.from(section.querySelectorAll('[data-product-media]'));
  let visibleCount = 0;

  mediaItems.forEach((media) => {
    const itemKey = media.dataset.mediaKey || '';
    const isVisible = mediaMatchesVariant(itemKey, mediaKey, matchMode);
    media.hidden = !isVisible;
    if (isVisible) visibleCount += 1;
  });

  if (visibleCount === 0) {
    mediaItems.forEach((media) => {
      media.hidden = false;
    });
  }

  updateProductGallery(section.querySelector('[data-product-gallery]'), 0, true);
});

document.addEventListener('input', (event) => {
  const quantityInput = event.target.closest('.main-product__quantity');
  if (!quantityInput) return;

  const section = quantityInput.closest('.shopify-section') || document;
  syncMobileProductAtc(section);
});
